import { ArrowUpRight, ClockCounterClockwise, MapPin, Sparkle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getStoryThumbnailUrl } from "@/utils/storyMedia";
import { getStoryCategoryData } from "@/utils/storyCategorization";
import { isVerticalStory } from "@/utils/videoEmbed";
import { getStoryDisplayTitle } from "@/utils/storySections";

const formatTime = (value) => {
  try {
    return new Date(value).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (error) {
    return value;
  }
};

const cleanAiCopy = (value) => value
  ?.replace(/\*\*/g, "")
  .replace(/__/g, "")
  .replace(/^#+\s*/gm, "")
  .replace(/\s*\[[^\]]+\]\s*/g, " ")
  .replace(/\s*\((sports|editorial)\)\s*/gi, " ")
  .replace(/[ \t]{2,}/g, " ")
  .trim();

export const StoryCard = ({ story, featured = false }) => {
  const navigate = useNavigate();
  const displayTitle = getStoryDisplayTitle(story);
  const verticalStory = isVerticalStory(story);
  const { sceneCategory, coverageArea } = getStoryCategoryData(story);
  const thumbnailUrl = getStoryThumbnailUrl(story);
  const showAiOverview = story.section === "breaking";
  const secondaryCopy = cleanAiCopy(story.ai_story) || story.caption;
  const visibleTags = (story.tags || []).slice(0, 3);

  const openStory = () => navigate(`/stories/${story.slug}`);
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openStory();
    }
  };
  const stopCardNavigation = (event) => event.stopPropagation();

  return (
    <Card
      className={`group h-full cursor-pointer overflow-hidden rounded-none border-[rgba(10,15,26,0.12)] bg-white shadow-none transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-lg ${featured ? "lg:min-h-[33rem]" : "min-h-[26rem]"}`}
      data-testid={`story-card-${story.slug}`}
      onClick={openStory}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className={`relative ${verticalStory ? "aspect-[4/5]" : featured ? "aspect-[4/3]" : "aspect-[16/10]"}`} data-testid={`story-card-image-${story.slug}`}>
        <img alt={displayTitle} className="news-image h-full w-full" src={thumbnailUrl} />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/65 via-slate-950/10 to-transparent" />
        <div className="absolute left-4 top-4 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-2 bg-[#D21404] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-white" data-testid={`story-card-section-${story.slug}`}>
            <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
            {story.section === "sports" ? "Sports" : story.section === "editorial" ? "What’s New" : "Breaking News"}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white sm:p-6">
          <p className="eyebrow text-white/80" data-testid={`story-card-source-${story.slug}`}>{story.source_title}</p>
          <h3 className={`headline-balance story-card-title mt-2 font-['Outfit'] ${featured ? "text-3xl font-black tracking-tight sm:text-4xl" : "text-2xl font-bold tracking-tight"}`} data-testid={`story-card-headline-${story.slug}`}>
            {displayTitle}
          </h3>
        </div>
      </div>
      <CardContent className="flex h-full flex-col gap-5 p-5 pt-5 sm:gap-6 sm:p-6" data-testid={`story-card-content-${story.slug}`}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-[rgba(10,15,26,0.08)] pb-4 text-sm text-slate-600">
          {story.author ? (
            <span className="inline-flex items-center gap-2" data-testid={`story-card-author-${story.slug}`}>
              By {story.author}
            </span>
          ) : null}
          <span className="inline-flex items-center gap-2" data-testid={`story-card-time-${story.slug}`}>
            <ClockCounterClockwise size={16} weight="bold" />
            {formatTime(story.published_at)}
          </span>
          <span className="inline-flex items-center gap-2" data-testid={`story-card-location-${story.slug}`}>
            <MapPin size={16} weight="bold" />
            {story.location}
          </span>
        </div>

        <div className="flex flex-wrap gap-2.5" data-testid={`story-card-category-group-${story.slug}`}>
          <span className="border border-[rgba(210,20,4,0.18)] bg-[rgba(210,20,4,0.06)] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#D21404]" data-testid={`story-card-scene-category-${story.slug}`}>
            {sceneCategory}
          </span>
          <span className="border border-[rgba(10,15,26,0.12)] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-700" data-testid={`story-card-coverage-area-${story.slug}`}>
            {coverageArea}
          </span>
        </div>

        <p className="copy-measure story-card-summary whitespace-pre-line text-sm leading-7 text-slate-700" data-testid={`story-card-excerpt-${story.slug}`}>{story.excerpt}</p>

        {secondaryCopy && secondaryCopy !== story.excerpt ? (
          <div className={`p-4 sm:p-5 ${showAiOverview ? "border border-[rgba(0,47,167,0.18)] bg-[rgba(0,47,167,0.04)]" : "border border-[rgba(10,15,26,0.08)] bg-[#F8F9FA]"}`} data-testid={`ai-story-card-${story.slug}`}>
            <div className={`mb-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] ${showAiOverview ? "text-[#1338BE]" : "text-slate-600"}`}>
              <Sparkle size={16} weight="fill" />
              {showAiOverview ? "Situation overview" : "More context"}
            </div>
            <p className="story-card-secondary-copy whitespace-pre-line text-sm leading-7 text-slate-700" data-testid={`story-card-ai-copy-${story.slug}`}>{secondaryCopy}</p>
          </div>
        ) : null}

        <div className="mt-auto flex flex-wrap gap-2.5">
          {visibleTags.map((tag) => (
            <span key={`${story.slug}-${tag}`} className="border border-[rgba(10,15,26,0.12)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-700" data-testid={`story-card-tag-${story.slug}-${tag}`}>
              {tag}
            </span>
          ))}
        </div>

        <div className="grid gap-3 pt-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
          <Button asChild className="w-full rounded-none bg-[#D21404] px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#B31203]" data-testid={`story-card-detail-link-${story.slug}`}>
            <Link to={`/stories/${story.slug}`}>
              {verticalStory ? "Watch Vertical Story" : "Open Story Page"}
            </Link>
          </Button>
          <Button asChild className="rounded-none border border-slate-950 bg-transparent px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-950 hover:bg-slate-950 hover:text-white sm:w-auto" data-testid={`story-card-link-${story.slug}`} variant="ghost">
            <a href={story.permalink || "#newsroom"} onClick={stopCardNavigation} rel="noreferrer" target="_blank">
              View Source
              <ArrowUpRight size={16} weight="bold" />
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};